import React from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { 
  Building2, 
  Users, 
  Search, 
  Settings, 
  Truck, 
  Heart, 
  MessageSquare, 
  FileText, 
  Calculator, 
  GraduationCap,
  Award,
  Star,
  Target,
  Hospital
} from "lucide-react";


const Services=()=>{
    const navigate = useNavigate();
    
  const stats = [
 { icon: Users, value: "100+", label: "Happy Clients" },
    { icon: Award, value: "200+", label: "Projects Completed" },
    { icon: Target, value: "8+", label: "Years Experience" },
    { icon: Star, value: "98%", label: "Success Rate" },
  ];

  return(
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-accent-600 text-white pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden">
        {/* Hexagonal Pattern Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-4 sm:left-10 w-12 h-12 sm:w-16 sm:h-16 bg-white transform rotate-45"></div>
          <div className="absolute top-40 right-4 sm:right-20 w-8 h-8 sm:w-12 sm:h-12 bg-white transform rotate-45"></div>
          <div className="absolute bottom-32 left-1/4 w-16 h-16 sm:w-20 sm:h-20 bg-white transform rotate-45"></div>
          <div className="absolute bottom-20 right-1/3 w-10 h-10 sm:w-14 sm:h-14 bg-white transform rotate-45"></div>
          <div className="absolute top-1/2 left-1/2 w-6 h-6 sm:w-8 sm:h-8 bg-white transform rotate-45"></div>
        </div>
        
        {/* Floating Service Icons */}
        <div className="absolute top-16 left-1/4 w-6 h-6 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center animate-bounce">
          <Building2 className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
        </div>
        <div className="absolute top-32 right-1/4 w-6 h-6 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center animate-bounce delay-1000">
          <Users className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
        </div>
        <div className="absolute bottom-40 left-1/3 w-6 h-6 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center animate-bounce delay-2000">
          <Settings className="h-4 w-4 text-white" />
        </div>
        
        <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6 sm:space-y-8"
            >
              <div className="space-y-4 sm:space-y-6">
                <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-white/20 backdrop-blur-sm text-white rounded-full text-xs sm:text-sm font-semibold border border-white/30">
                  <Star className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                  Comprehensive Solutions
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                  Expert <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">Consulting</span> Services
                </h1>
                <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto px-4">
Transform your organization your organization with our tailored suite of professional services. From strategic management consulting to specialized technical support, we deliver impactful, results-driven solutions.
                </p>
              </div>
              
              {/* Service Highlights */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 sm:gap-6 mt-8 sm:mt-12">
                {[
                  { icon: Building2, label: "NGO Consultancy" },
                  { icon: Users, label: "HR Solutions" },
                  { icon: Search, label: "Research & Studies" },
                  { icon: Settings, label: "Technical Support" },
                  { icon: Hospital, label: "Health & Hospital" }
                ].map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2"
                  >
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <service.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-sm text-white font-medium text-center">{service.label}</div>
                </motion.div>
              ))}
            </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-600">Comprehensive</span> Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our full range of professional consulting services designed to meet your organization's unique needs and challenges.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Building2,
                title: "NGO Consultancy",
                description: "One-stop solution for NGOs across India including business development, proposal writing, documentation, and digital support for institutional strengthening.",
                link: "/ngo-consultancy"
              },
              {
                icon: Users,
                title: "HR Solutions",
                description: "Quality human resource solutions with proven track record of providing stable, qualified staff through rigorous due diligence processes.",
                link: "/hr-solutions"
              },
              {
                icon: Search,
                title: "Research & Studies",
                description: "Expert research support including questionnaire design, data collection (qualitative & quantitative), analysis, and quality report preparation.",
                link: "/research-studies"
              },
              {
                icon: Settings,
                title: "Technical Assistance",
                description: "Comprehensive technical support for planning, execution, MIS development, and monitoring of national programs and schemes.",
                link: "/technical-assistance"
              },
              {
                icon: Truck,
                title: "Supply Chain Management",
                description: "Streamlined and cost-effective supply chain solutions that maximize customer value and provide competitive marketplace advantages.",
                link: "/supply-chain"
              },
              {
                icon: Heart,
                title: "CSR Solutions",
                description: "Expertise in project formulation, implementation, and assessment to support corporate social responsibility initiatives across sectors.",
                link: "/csr-solutions"
              },
              {
                icon: MessageSquare,
                title: "SBCC Services",
                description: "Social and Behaviour Change Communication strategies, planning, and development of innovative, cost-effective IEC materials.",
                link: "/sbcc-services"
              },
              {
                icon: FileText,
                title: "Documentation & Translation",
                description: "Comprehensive documentation support including annual reports, case studies, process documentation, translation, and proofreading services.",
                link: "/documentation"
              },
              {
                icon: Calculator,
                title: "Financial Compliance",
                description: "Complete financial and statutory compliance solutions including audit reports, registrations, certifications, and regulatory support.",
                link: "/financial-compliance"
              },
              {
                icon: GraduationCap,
                title: "Education & Training",
                description: "Training design, module development, community education programs, and skill development initiatives for employment and behavior change.",
                link: "/education-training"
              },
              {
                icon: Hospital,
                title: "Health and Hospital",
                description: "Expert-led hospital assessments (NABH, ISO, NQAS, LaQshya, Kayakalp) with strategic accreditation support and adaptive healthcare solutions.",
                link: "/health-and-hospital"
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-8 h-full border border-gray-200 hover:border-primary-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <service.icon className="h-8 w-8 text-white" />
            </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {service.description}
                      </p>
          </div>
                    
                    <div className="pt-4">
                      <Link to={service.link} className="block">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="w-full bg-gradient-to-r from-primary-600 to-accent-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                        >
                          Learn More
                        </motion.button>
                      </Link>
            </div>
          </div>
            </div>
              </motion.div>
            ))}
          </div>
            </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Transform Your Organization?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-white/90">
              Let's discuss how our expert consulting services can help you achieve your goals and drive meaningful impact.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
              onClick={()=>navigate('/contactus')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get Started Today
              </motion.button>
              <motion.button
                  onClick={()=>navigate('/projects')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-primary-600 transition-all duration-300"
              >
                View Our Portfolio
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
export default Services;